import { useCallback } from 'react';
import { showInfo, showError } from '../../../shared/ui/appToast';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';
import {
  isInventoriableProduct,
  getLastStockMovementUnitCost,
  getStockLotExpirySplit,
  type StockMovementReasonKey,
} from '../../../inventory/stock/public-api';
import { getProductById } from '../../../catalog/infra-api';
import type { Product } from '../../../catalog/domain-api';
import type { StockRow } from '../../../inventory/stock/public-api';
import { useInventoryUiStore } from '../../store/index';

interface UseInventoryModalsParams {
  canEdit: boolean;
  canAdjust: boolean;
  canViewValuation: boolean;
  currentBranchId: string | null;
  stocks: StockRow[];
  openCatalog: () => void;
  setLastUnitCost: (cost: number | null) => void;
}

export const useInventoryModals = (params: UseInventoryModalsParams) => {
  const { canEdit, canAdjust, canViewValuation, currentBranchId, stocks, openCatalog, setLastUnitCost } = params;
  const purchasesEnabled = useHasBusinessCapability('purchases');

  // Transfer Store
  const setTransferModalVisible = useInventoryUiStore((s) => s.setTransferModalVisible);
  const setSelectedStock = useInventoryUiStore((s) => s.setSelectedStock);
  const setAdjustStockRow = useInventoryUiStore((s) => s.setAdjustStockRow);
  const setAdjustDelta = useInventoryUiStore((s) => s.setAdjustDelta);
  const setAdjustReason = useInventoryUiStore((s) => s.setAdjustReason);
  const setAdjustModalVisible = useInventoryUiStore((s) => s.setAdjustModalVisible);
  const setCostCorrectStockRow = useInventoryUiStore((s) => s.setCostCorrectStockRow);
  const setCostCorrectInput = useInventoryUiStore((s) => s.setCostCorrectInput);
  const setCostCorrectModalVisible = useInventoryUiStore((s) => s.setCostCorrectModalVisible);

  // Add Stock Store
  const setSelectedProduct = useInventoryUiStore((s) => s.setSelectedProduct);
  const setProductQuery = useInventoryUiStore((s) => s.setProductQuery);
  const setProductQueryDebounced = useInventoryUiStore((s) => s.setProductQueryDebounced);
  const setProductResults = useInventoryUiStore((s) => s.setProductResults);
  const setProductPageOffset = useInventoryUiStore((s) => s.setProductPageOffset);
  const setProductHasMore = useInventoryUiStore((s) => s.setProductHasMore);
  const setEntryQuantity = useInventoryUiStore((s) => s.setEntryQuantity);
  const setEntryUnitCost = useInventoryUiStore((s) => s.setEntryUnitCost);
  const setEntryMinQuantity = useInventoryUiStore((s) => s.setEntryMinQuantity);
  const setEntryExpiry = useInventoryUiStore((s) => s.setEntryExpiry);
  const setEntryReason = useInventoryUiStore((s) => s.setEntryReason);
  const setEntrySuggestion = useInventoryUiStore((s) => s.setEntrySuggestion);
  const setEntryScanFeedback = useInventoryUiStore((s) => s.setEntryScanFeedback);
  const setEntrySupplierId = useInventoryUiStore((s) => s.setEntrySupplierId);
  const setEntryLotNumber = useInventoryUiStore((s) => s.setEntryLotNumber);
  const setEntryVariantId = useInventoryUiStore((s) => s.setEntryVariantId);
  const setAddStockVisible = useInventoryUiStore((s) => s.setAddStockVisible);

  // Modals Store
  const setActivityModalVisible = useInventoryUiStore((s) => s.setActivityModalVisible);

  const openAddStockModal = useCallback(() => {
    if (!canEdit) {
      showInfo('Solo lectura', 'Podés ver el stock, no ingresar mercadería.');
      return;
    }
    setSelectedProduct(null);
    setProductQuery('');
    setProductQueryDebounced('');
    setProductResults([]);
    setProductPageOffset(0);
    setProductHasMore(false);
    setEntryQuantity('');
    setEntryUnitCost('');
    setEntryMinQuantity('0');
    setEntryExpiry('');
    setEntryReason(purchasesEnabled ? 'stock_restock' : 'purchase_reception');
    setEntrySuggestion(null);
    setEntryScanFeedback(null);
    setLastUnitCost(null);
    setEntryLotNumber('');
    setEntryVariantId(null);
    setAddStockVisible(true);
  }, [
    canEdit,
    setSelectedProduct,
    setProductQuery,
    setProductQueryDebounced,
    setProductResults,
    setProductPageOffset,
    setProductHasMore,
    setEntryQuantity,
    setEntryUnitCost,
    setEntryMinQuantity,
    setEntryExpiry,
    setEntryReason,
    setEntrySuggestion,
    setEntryScanFeedback,
    setLastUnitCost,
    setEntryLotNumber,
    setEntryVariantId,
    setAddStockVisible,
    purchasesEnabled,
  ]);

  const openAddStockModalForProduct = useCallback(
    async (product: Product) => {
      if (!isInventoriableProduct(product)) {
        showInfo('Servicio sin stock', 'Los servicios no llevan stock.');
        return;
      }
      openAddStockModal();
      setSelectedProduct(product);
      setProductQuery(product.sku);
      const existingStock = stocks.find((row) => row.productId === product.id);
      setEntryMinQuantity(existingStock ? String(existingStock.minQuantity) : '0');
      setEntrySupplierId(product.preferredSupplierId ?? null);
      if (currentBranchId) {
        const lastCost = await getLastStockMovementUnitCost(product.id, currentBranchId);
        setLastUnitCost(lastCost);
      }
    },
    [openAddStockModal, setSelectedProduct, setProductQuery, stocks, setEntryMinQuantity, setEntrySupplierId, currentBranchId, setLastUnitCost]
  );

  const openAddStockForStockRow = useCallback(
    async (row: StockRow) => {
      if (!canEdit) {
        showInfo('Solo lectura', 'Podés ver el stock, no ingresar mercadería.');
        return;
      }
      try {
        const product = await getProductById(row.productId);
        if (!product) {
          showError('Producto no encontrado', 'No pudimos ubicar el producto en el catálogo.');
          return;
        }
        await openAddStockModalForProduct(product);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'No pudimos abrir el ingreso.';
        showError('Error', message);
      }
    },
    [canEdit, openAddStockModalForProduct]
  );

  const closeAddStockModal = useCallback(() => {
    setAddStockVisible(false);
    setSelectedProduct(null);
    setEntrySuggestion(null);
    setEntryScanFeedback(null);
    setEntrySupplierId(null);
    setEntryLotNumber('');
    setEntryVariantId(null);
  }, [setAddStockVisible, setSelectedProduct, setEntrySuggestion, setEntryScanFeedback, setEntrySupplierId, setEntryLotNumber, setEntryVariantId]);

  const openAdjustModal = useCallback(
    (row: StockRow, prefill?: { delta: string; reason: StockMovementReasonKey }) => {
      if (!canAdjust) {
        showInfo('Solo lectura', 'Podés ver el stock, no ajustarlo.');
        return;
      }
      setAdjustStockRow(row);
      setAdjustDelta(prefill?.delta ?? '');
      setAdjustReason(prefill?.reason ?? 'shrinkage');
      setAdjustModalVisible(true);
    },
    [canAdjust, setAdjustStockRow, setAdjustDelta, setAdjustReason, setAdjustModalVisible]
  );

  // #1497: baja explícita de unidades vencidas — abre el ajuste precargado
  // con la cantidad vencida (topeada al stock físico) y motivo Vencimiento.
  const openExpiryWriteOffModal = useCallback(
    (row: StockRow) => {
      const expiredQty = getStockLotExpirySplit(row.productId, row.branchId)?.expiredQuantity ?? 0;
      const writable = Math.min(expiredQty, Math.max(row.quantity, 0));
      if (writable <= 0) return;
      openAdjustModal(row, {
        delta: `-${String(writable).replace('.', ',')}`,
        reason: 'expiry_writeoff',
      });
    },
    [openAdjustModal]
  );

  const closeAdjustModal = useCallback(() => {
    setAdjustModalVisible(false);
    setAdjustStockRow(null);
  }, [setAdjustModalVisible, setAdjustStockRow]);

  const openCostCorrectModal = useCallback(
    (row: StockRow) => {
      if (!canViewValuation) {
        showInfo('Solo lectura', 'No tenés permiso para corregir el costo.');
        return;
      }
      setCostCorrectStockRow(row);
      setCostCorrectInput(row.avgCost > 0 ? String(row.avgCost).replace('.', ',') : '');
      setCostCorrectModalVisible(true);
    },
    [canViewValuation, setCostCorrectStockRow, setCostCorrectInput, setCostCorrectModalVisible]
  );

  const closeCostCorrectModal = useCallback(() => {
    setCostCorrectModalVisible(false);
    setCostCorrectStockRow(null);
    setCostCorrectInput('');
  }, [setCostCorrectModalVisible, setCostCorrectStockRow, setCostCorrectInput]);

  const openActivityModal = useCallback(() => {
    setActivityModalVisible(true);
  }, [setActivityModalVisible]);

  const closeActivityModal = useCallback(() => {
    setActivityModalVisible(false);
  }, [setActivityModalVisible]);

  return {
    openAddStockModal,
    openAddStockModalForProduct,
    openAddStockForStockRow,
    closeAddStockModal,
    openAdjustModal,
    openExpiryWriteOffModal,
    closeAdjustModal,
    openCostCorrectModal,
    closeCostCorrectModal,
    openActivityModal,
    closeActivityModal,
    openCatalog,
  };
};
